import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Compact "Switch user" control rendered INSIDE GlobalHeader.
 * - Visible to STAS (is_root) only.
 * - Hidden globally when env REACT_APP_SHOW_DEV_SWITCHER === "false".
 *   (Easy single-line kill switch when you no longer want it in production.)
 * - Uses the STAS-only impersonation endpoint POST /api/users/{id}/switch
 *   (so it works even when DEV_USER_SWITCH_ENABLED is off on the backend).
 * - Every switch is recorded in audit_logs server-side.
 */
// PIN required for non-root (employees) to use Switch User.
// Hardcoded for now per STAS request — will be moved to Work Rules later.
const SWITCH_USER_PIN = "506";

export default function DevUserSwitcher() {
  const { user, refreshMe } = useAuth();
  const { theme, dir, lang } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);

  const enabled = process.env.REACT_APP_SHOW_DEV_SWITCHER !== "false";
  const stasOnly = process.env.REACT_APP_DEV_SWITCHER_STAS_ONLY === "true";
  const isRoot = !!user?.role?.is_root;
  const isInternal = user?.user_type === "internal";

  // PIN gate state — only relevant for non-root users
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinErr, setPinErr] = useState("");
  const pinRequired = !isRoot;

  useEffect(() => {
    if (!open || users.length || !user) return;
    if (pinRequired && !pinUnlocked) return; // wait for PIN unlock
    // STAS can hit /users (full list). Others use the dev-only listing endpoint.
    const endpoint = user?.role?.is_root ? "/users" : "/dev/users";
    // Exclude the isolated ATS "data_entry_associate" role — it is NOT a real
    // operational account and must never appear in the user switcher.
    const EXCLUDED_ROLES = new Set(["data_entry_associate"]);
    api.get(endpoint)
      .then(({ data }) => setUsers((data || []).filter(
        (u) => u.user_type === "internal"
            && u.is_active
            && !EXCLUDED_ROLES.has(String(u.role?.code || "").toLowerCase())
      )))
      .catch(() => setUsers([]));
  }, [open, users.length, user, pinRequired, pinUnlocked]);

  // Close on outside click — also resets PIN state for security
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setPinUnlocked(false);
        setPinInput("");
        setPinErr("");
      }
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const submitPin = () => {
    if (pinInput.trim() === SWITCH_USER_PIN) {
      setPinUnlocked(true);
      setPinErr("");
      setPinInput("");
    } else {
      setPinErr(lang === "ar" ? "الرمز غير صحيح" : "Incorrect PIN");
      setPinInput("");
    }
  };

  if (!enabled || !user || !isInternal) return null;
  if (stasOnly && !isRoot) return null;

  const pick = async (targetId) => {
    if (targetId === user.id) { setOpen(false); return; }
    setBusy(true); setErr("");
    try {
      // STAS uses /api/users/{id}/switch (audit-only). Others use /api/dev/switch-user
      // (gated by DEV_USER_SWITCH_ENABLED on the backend). Both record an audit entry.
      const endpoint = isRoot ? `/users/${targetId}/switch` : "/dev/switch-user";
      const body = isRoot ? undefined : { user_id: targetId };
      const { data } = await api.post(endpoint, body);
      localStorage.setItem("vta_token", data.access_token);
      await refreshMe();
      setOpen(false);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const T = lang === "ar"
    ? { btn: "تبديل المستخدم", head: "اختر مستخدماً", pinHead: "أدخل الرمز السري", pinPh: "••••", pinSubmit: "متابعة", pinHint: "مطلوب للموظفين فقط" }
    : { btn: "Switch user", head: "Pick a user", pinHead: "Enter PIN", pinPh: "••••", pinSubmit: "Continue", pinHint: "Required for employees only" };

  const css = {
    wrap: { position: "relative" },
    btn: {
      background: "transparent",
      color: theme.text,
      border: `1px solid ${theme.border}`,
      padding: "7px 12px",
      borderRadius: "999px",
      cursor: "pointer",
      fontSize: "11px",
      fontWeight: 700,
      whiteSpace: "nowrap",
      display: "inline-flex",
      gap: "6px",
      alignItems: "center",
    },
    dot: {
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      background: theme.gold,
      boxShadow: `0 0 6px ${theme.gold}`,
    },
    panel: {
      position: "absolute",
      top: "42px",
      [dir === "rtl" ? "left" : "right"]: 0,
      width: "260px",
      maxHeight: "380px",
      overflowY: "auto",
      background: theme.surface,
      border: `1px solid ${theme.borderAccent}`,
      borderRadius: "12px",
      boxShadow: theme.cardShadow,
      zIndex: 9000,
    },
    head: {
      padding: "10px 14px",
      fontSize: "11px",
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.borderSoft}`,
      background: theme.headerBg,
      position: "sticky",
      top: 0,
      letterSpacing: "0.4px",
    },
    item: (active) => ({
      display: "flex",
      flexDirection: "column",
      alignItems: dir === "rtl" ? "flex-end" : "flex-start",
      width: "100%",
      padding: "10px 14px",
      border: "none",
      borderBottom: `1px solid ${theme.borderSoft}`,
      cursor: busy ? "wait" : "pointer",
      textAlign: dir === "rtl" ? "right" : "left",
      color: theme.text,
      background: active
        ? (theme.name === "dark" ? "rgba(212,175,55,0.15)" : "rgba(184,144,42,0.12)")
        : "transparent",
      opacity: busy ? 0.6 : 1,
    }),
    name: { fontSize: "13px", fontWeight: 800 },
    role: { fontSize: "10px", color: theme.textMuted, marginTop: "2px" },
    err: { padding: "10px 14px", color: theme.errorText, fontSize: "11px" },
  };

  return (
    <div style={css.wrap} ref={ref} data-testid="dev-user-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={css.btn}
        data-testid="dev-switch-toggle"
      >
        <span style={css.dot} aria-hidden />
        {T.btn}
      </button>
      {open && (
        <div style={css.panel} data-testid="dev-switch-panel">
          {pinRequired && !pinUnlocked ? (
            <div data-testid="dev-switch-pin-gate" style={{ padding: "14px" }}>
              <div style={{ ...css.head, padding: 0, border: 0, marginBottom: "8px" }}>
                {T.pinHead}
              </div>
              <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "10px" }}>
                {T.pinHint}
              </div>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={pinInput}
                onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, "").slice(0, 6)); setPinErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") submitPin(); }}
                placeholder={T.pinPh}
                data-testid="dev-switch-pin-input"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: `1px solid ${pinErr ? theme.errorText : theme.border}`,
                  background: theme.surface,
                  color: theme.text,
                  fontSize: "16px",
                  textAlign: "center",
                  letterSpacing: "8px",
                  fontWeight: 800,
                  outline: "none",
                }}
              />
              {pinErr && (
                <div data-testid="dev-switch-pin-error" style={{ ...css.err, padding: "8px 0 0 0" }}>
                  {pinErr}
                </div>
              )}
              <button
                type="button"
                onClick={submitPin}
                data-testid="dev-switch-pin-submit"
                style={{
                  marginTop: "10px",
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: "10px",
                  border: `1px solid ${theme.borderAccent}`,
                  background: theme.gold,
                  color: "#0a0a0a",
                  fontSize: "12px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {T.pinSubmit}
              </button>
            </div>
          ) : (
            <>
              <div style={css.head}>{T.head}</div>
              {users.map((u) => {
                const roleLabel = (lang === "en" ? u.role?.name_en : u.role?.name_ar) || u.role?.name_ar || "—";
                return (
                  <button
                    key={u.id}
                    onClick={() => pick(u.id)}
                    disabled={busy}
                    data-testid={`dev-switch-${u.username || u.id}`}
                    style={css.item(u.id === user.id)}
                  >
                    <div style={css.name}>{u.full_name}</div>
                    <div style={css.role}>@{u.username || "—"} • {roleLabel}</div>
                  </button>
                );
              })}
              {err && <div style={css.err}>{err}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
